import { api } from "api";
import { useAuth } from "auth/useAuth";
import { useEffect } from "react";
import { useMasonFlag } from "utils/useMasonFlag";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import dayjs from "dayjs";
import type { QueryClient } from "@tanstack/react-query";
import type { SyncStatusQueryNonEmptyResult, SyncStatusQueryResult } from "api/responses";

/** PMS integration sync status string. */
export type SyncStatus = "current" | "recent" | "inprogress" | "timedout" | "";

/** Get PMS integration sync status string. */
export function useSyncStatus(disabled: boolean) {
    const { data } = useSyncStatusQuery(disabled);
    const loginResult = useAuth((state) => state.loginResult);
    return disabled ? "" : calcSyncStatus(data, loginResult?.syncRequestedAt);
}

/** Call API to get PMS integration sync status. */
function useSyncStatusQuery(disabled: boolean) {
    const flag = useMasonFlag();
    const queryClient = useQueryClient();
    const syncRequestedAt = useSyncRequestedAt();

    const result = useQuery({
        queryKey: ["syncStatus", syncRequestedAt],
        queryFn: async (context) => await api.provider.syncStatus(context.signal),
        enabled: isDate(syncRequestedAt) && !disabled && flag,
        refetchInterval: (qry) => {
            const result = qry.state.data;
            if (!result) {
                return false;
            }

            // refetch often at first, then not at all
            const syncStatusString = calcSyncStatus(result, syncRequestedAt);
            const syncRequestedAgo = dayjs(result.now).diff(syncRequestedAt);
            return calcRefetchInterval(syncRequestedAgo, syncStatusString);
        },
    });

    useInvalidate(queryClient, result.data);
    return result;
}

function useSyncRequestedAt() {
    const loginResult = useAuth((state) => state.loginResult);
    const syncRequestedAt = dateOrZero(loginResult?.syncRequestedAt);
    return syncRequestedAt;
}

type Result = Readonly<SyncStatusQueryResult>;
type NonEmptyResult = Readonly<SyncStatusQueryNonEmptyResult>;

export function calcSyncStatus(result: Result | undefined, syncRequestedAt: number | undefined): SyncStatus {
    if (!result || !isDate(syncRequestedAt)) {
        return "";
    } else if (isInProgress(result)) {
        return "inprogress";
    } else {
        return calcSyncStatusNotInProgress(result, syncRequestedAt);
    }
}

function isDate(x: number | null | undefined): x is number {
    return typeof x === "number" && x > 0;
}

const isInProgress = (result: Result) => !!result && !result.finishDate && result.now < result.dueDate;

function calcSyncStatusNotInProgress(result: NonEmptyResult, syncRequestedAt: number): SyncStatus {
    if (!isDate(result.finishDate)) return "timedout";

    const syncRequestedMoment = dayjs(syncRequestedAt);
    const finishMoment = dayjs(result.finishDate);
    const diff = finishMoment.diff(syncRequestedMoment);
    return diff > -2000 ? "current" : diff >= -75000 ? "recent" : "";
}

function calcRefetchInterval(syncRequestedAgo: number, syncStatusString: string) {
    return syncRequestedAgo < 30000 || syncStatusString === "inprogress" ? 1000 : false;
}

function useInvalidate(queryClient: QueryClient, data: Readonly<SyncStatusQueryResult> | undefined) {
    const vyneSyncDate = dateOrZero(data?.vyneSyncDate);

    useEffect(() => {
        if (vyneSyncDate) {
            void queryClient.invalidateQueries({
                queryKey: ["searchPatientField"],
            });
        }
    }, [queryClient, vyneSyncDate]);
}

export const dateOrZero = (x: number | null | undefined) => (isDate(x) ? x : 0);
